import { Listbox } from '@headlessui/react';
import { useRouter } from 'next/dist/client/router';
import { ChangeEvent, Dispatch, useEffect, useMemo, useState } from 'react';
import ChevronUp from '../../icons/ChevronUp';
import MinimumSalarySlider from '../MinimumSalarySlider';
import { removeNonNumericCharacters } from 'wwg-utilities';
import AgeFilterOptions from '../AgeFilterOptions';
import StandardFilterOptions from '../StandardFilterOptions';
import { SingleFilter } from '../../../lib/types';
import { isSkillup, isWhereWeGo } from '../../../lib/config/project/project';
import useFilter from '../../../lib/hooks/filters';

interface MobileFilterProps {
  filterOptions: string[];
  paramLabel: string;
  label: string;
  isLoading?: boolean;
  potentialNumberOfResults: number;
  updatePotentialFilterForParam: (filter: SingleFilter) => void;
  setActive: Dispatch<boolean>;
  seeAllResults: () => void;
  isCareers?: boolean;
}

const MobileFilter: React.FC<MobileFilterProps> = ({
  paramLabel,
  label,
  filterOptions,
  potentialNumberOfResults,
  isCareers,
  updatePotentialFilterForParam,
  setActive,
  seeAllResults,
}) => {
  const router = useRouter();
  const defaultValue = useMemo(() => {
    switch (paramLabel) {
      case 'distance':
        return ['25 miles'];
      default:
        return []
    }
  }, [])
  const [selectedFilters, setSelectedFilters] = useState(defaultValue as string[]);

  // Update potential filters based on selection
  const { onChange } = useFilter(
    selectedFilters,
    paramLabel,
    setSelectedFilters,
    updatePotentialFilterForParam,
  );

  // Initialized the filters based on query params
  useEffect(() => {
    const { query } = router;
    const filters = query[paramLabel] as string;
    setSelectedFilters(filters?.split('_') || []);
  }, [router]);

  const manualUpdate = (value: string) => {
    setSelectedFilters([value]);
  };

  const onDone = () => {
    setActive(false);
    seeAllResults();
  };

  const textClass =
    isSkillup || (isWhereWeGo && !isCareers)
      ? 'text-primary'
      : 'text-careerPageHeading';

  const bgClass =
    isSkillup || (isWhereWeGo && !isCareers)
      ? 'bg-primary'
      : 'bg-careerPageHeading';
  const borderClass =
    isSkillup || (isWhereWeGo && !isCareers)
      ? 'border-primary'
      : 'border-careerPageHeading';



  return (
    <div className="h-full">
      <div className="grid items-center w-full grid-cols-2 grid-rows-1 p-2 -my-4">
        <ChevronUp
          onClick={onDone}
          className="w-6 h-6 col-start-1 row-start-1 mr-4 transform -rotate-90 text-gray8"
        />
        <h2 className="w-full col-start-1 col-end-3 row-start-1 text-lg font-medium text-center text-gray1">
          {label}
        </h2>
      </div>
      <hr className="my-6 -mx-2 border-1 text-gray" />
      <Listbox
        onChange={onChange}
        className="relative px-4 overflow-scroll max-h-[50vh] border-b-gray border-b-2"
        value={selectedFilters}
        multiple
        as="div"
      >
        {paramLabel == 'salary' ? (
          <MinimumSalarySlider
            options={filterOptions}
            onChange={manualUpdate}
            setSelectedFilters={setSelectedFilters}
            selectedFilters={selectedFilters}
          />
        ) : paramLabel == 'age' ? (
          <AgeFilterOptions
            value={selectedFilters.join('')}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const val =
                removeNonNumericCharacters(event.target.value)?.toString() ||
                '';

              if (val != 'NaN') {
                setSelectedFilters([val]);
              } else {
                setSelectedFilters(['']);
              }
            }}
          />
        ) : (
          <StandardFilterOptions
            filterOptions={filterOptions}
            paramLabel={paramLabel}
            singleSelect={paramLabel == 'distance'}
          ></StandardFilterOptions>
        )}
      </Listbox>
      <div className="absolute bottom-0 grid grid-cols-1 gap-4 pb-24 left-4 right-4">
        <p className="text-center">{potentialNumberOfResults} Results</p>
        <button
          className={`w-full p-4 text-lg font-bold border-solid rounded-md ${textClass} border-1 ${borderClass} font-base`}
          onClick={() => setSelectedFilters([])}
          tabIndex={1}
        >
          Clear
        </button>
        <button
          className={`w-full p-4 text-lg font-bold text-white rounded-lg ${bgClass} font-base`}
          onClick={onDone}
          tabIndex={1}
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default MobileFilter;
